<template>
  <div class="p-0 d-flex justify-content-end">
    <transition name="expand-push" mode="out-in">
      <div v-if="userModules.comments && presentationStore.visibleTab === 'Comment'" key="comments" class="full-lg">
        <Comments class="side-bar-tab " @showNav="showNav" />
      </div>
      <div v-else-if="userModules.amendments && presentationStore.visibleTab === 'Amendment'" key="amendments"
        class="full-lg d-flex flex-column flex-grow-1">
        <Amendments class="side-bar-tab" @showNav="showNav" />
      </div>
      <div v-else-if="userModules.candidacies && presentationStore.visibleTab === 'Candidacy'" key="candidacies"
        class="full-lg d-flex flex-column flex-grow-1">
        <Candidacies class="side-bar-tab" @showNav="showNav" />
      </div>
      <div v-else-if="presentationStore.visibleTab === 'Locales'" key="locales" class="full-lg d-flex flex-column flex-grow-1">
        <LocaleTab class="side-bar-tab" @showNav="showNav" />
      </div>
    </transition>

    <div class="d-flex flex-column justify-content-between m-0 text-center bg-primary-90"
      style="position: relative; z-index: 2; min-width: 100px; max-width: 100px;">
      <div>
        <div>
          <SideBarButton v-if="userModules.comments" @click="showTab('Comment')" :isActive="presentationStore.visibleTab === 'Comment'"
            :unread="unreadComments" :text="$t('js.sidebar.headers.comments')" icon="comment" />
          <SideBarButton v-if="userModules.amendments" @click="showTab('Amendment')"
            :isActive="presentationStore.visibleTab === 'Amendment'" :unread="unreadAmendments"
            :text="$t('js.sidebar.headers.amendments')" icon="file-signature" />
          <SideBarButton v-if="userModules.candidacies" @click="showTab('Candidacy')"
            :isActive="presentationStore.visibleTab === 'Candidacy'" :unread="unreadCandidacies"
            :text="$t('js.sidebar.headers.candidacies')" icon="user" />
          <SideBarButton data-bs-toggle="modal" data-bs-target="#highlightModal" :isActive="votingModulesStore.activeHighlight != null"
            :color="votingModulesStore.activeHighlight ? 'btn-success' : 'btn-secondary'" @click="votingModulesStore.setPostToHighlight(null)"
            :text="$t('js.sidebar.headers.highlight')" icon="thumbtack" :pulse="!!votingModulesStore.activeHighlight" />
        </div>
      </div>
      <div>
        <VoterCounts />
        <a href="." class="w-100 btn rounded-0 py-3 btn-theme-danger" style="line-height: 1em">
          <AVIcon icon="door-open" /><br>
          <span class="small text-truncate">{{ $t("js.sidebar.general.exit_mode") }}</span>
        </a>
      </div>
    </div>
    <HighlightModal />

    <Modal :visible="!!votingModulesStore.postToAddToBallot" @close="votingModulesStore.setPostToAddToBallot(null)" class="w-100 h-100" :maxWidth="'800px'">
      <template v-slot:header>
        <h4 class="mb-0">{{ $t("js.sidebar.candidacies.accept_and_add") }}</h4>
      </template>
      <template v-slot:body>
        <iframe :src="addCandidacyPath" class="w-100 h-100" frameBorder="0" style="min-height: 300px" id="addCandidacy">

        </iframe>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import Comments from "./Comments.vue"
import Candidacies from "./Candidacies.vue"
import Amendments from "./Amendments.vue"
import LocaleTab from "@/components/shared/LocaleTab.vue"
import HighlightModal from "../HighlightModal.vue"
import SideBarButton from "@/components/frontend/voting/sidebar/SideBarButton.vue"
import VoterCounts from "@/components/shared/VoterCounts.vue"
import Modal from "@/components/shared/Modal.vue"
import { SoundEffectPlayer } from "@/entrypoints/shared/soundEffects";
import { useSharedStore } from "@/entrypoints/stores/shared";
import { usePresentationStore } from "@/entrypoints/stores/presentation";
import { useVotingModulesStore } from "@/entrypoints/stores/voting_modules";
import type { Locale, UserPost, LiveTab } from "@/types";

const sharedStore = useSharedStore();

const presentationStore = usePresentationStore();

const votingModulesStore = useVotingModulesStore();

const navVisible = ref(false);

const soundEffectPlayer = ref(new SoundEffectPlayer({ volume: 0.5 }));

const userModules = computed(() => sharedStore.election.userModules);

const unreadComments = computed(() => {
  if (presentationStore.visibleTab === "Comment") return 0;
  return votingModulesStore.posts.filter((p: UserPost) => p.type === "Comment" && p.unread).length;
});

const unreadAmendments = computed(() => 
  votingModulesStore.posts.filter((p: UserPost) => p.type === "Amendment" && p.unread).length);

const unreadCandidacies = computed(() =>
  votingModulesStore.posts.filter((p: UserPost) => p.type === "Candidacy" && p.unread).length);

const addCandidacyPath = computed(() => {
  return votingModulesStore.postToAddToBallot ? `../contests/${votingModulesStore.postToAddToBallot.target_slide.contestId}/options/simple_new?post_id=${votingModulesStore.postToAddToBallot.id}&${sharedStore.locales.map((locale: Locale) => `option[title][${locale}]=${votingModulesStore.postToAddToBallot.owner.name}`).join("&")}` : "";
});

const showTab = (tab: LiveTab) => {
  navVisible.value = false;
  if (tab === presentationStore.visibleTab) {
    presentationStore.setVisibleTab("Presentation");
  } else {
    presentationStore.setVisibleTab(tab);
  }
};

const showNav = () => navVisible.value = !navVisible.value;

watch(unreadAmendments, (newCount, oldCount) => {
  if (newCount > oldCount) soundEffectPlayer.value.playAmendmentNotification();
});

watch(unreadCandidacies, (newCount, oldCount) => {
  if (newCount > oldCount) soundEffectPlayer.value.playCandidacyNotification();
});

onMounted(() => presentationStore.fetchLatestConfig());
</script>
